import React from 'react';
import PropTypes from 'prop-types';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';

/**
 * Recherche le conteneur scrollable du widget selon une cascade de priorites.
 * Doit etre appele AVANT un changement de page car apres le re-render
 * le conteneur peut ne plus etre scrollable si la nouvelle page a moins d'elements.
 *
 * @return {Element|null} Element scrollable trouve ou null
 */
function findScrollContainer() {
  // 1. Verifier si le document (iframe body) est le conteneur de scroll
  // En mode floating, le WidgetContainer a height:auto donc le scroll est sur le document
  const docScroller = document.scrollingElement || document.documentElement;
  if (docScroller.scrollHeight > docScroller.clientHeight) {
    return docScroller;
  }

  // 2. Chercher un conteneur MUI avec overflow scroll (mode embedded/fixed)
  const scrollableBoxes = document.querySelectorAll('[class*="MuiBox-root"]');
  for (const box of scrollableBoxes) {
    if (box.scrollHeight > box.clientHeight) {
      const style = window.getComputedStyle(box);
      const hasOverflowScroll = style.overflowY === 'auto' || style.overflowY === 'scroll';
      if (hasOverflowScroll) {
        return box;
      }
    }
  }

  // 3. Fallback vers le conteneur widget
  const widgetContainer = document.querySelector('[data-widget-type="giftvouchers"]');
  if (widgetContainer) return widgetContainer;

  return null;
}

function GiftVoucherPagination({page, totalPages, onChange, primaryColor, t}) {
  const handlePageChange = (event, newPage) => {
    // Scroll vers le haut AVANT le changement de page pour eviter le flash
    // (l'utilisateur est deja en haut quand le nouveau contenu apparait)
    const scrollElement = findScrollContainer();

    if (scrollElement) {
      scrollElement.scrollTo({top: 0});
    } else {
      window.scrollTo({top: 0});
    }

    onChange(event, newPage);
  };

  if (totalPages <= 1) return null;
  return (
    <Stack component="nav" role="navigation" aria-label={t ? t('widgets.giftVoucher.pagination.ariaLabel') : 'Pagination des bons cadeaux'} alignItems="center" sx={{mt: 2, mb: 2}}>
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        getItemAriaLabel={(type, pageNum, selected) => {
          if (type === 'page') {
            return t ? (selected ? t('widgets.giftVoucher.pagination.current', {page: pageNum}) : t('widgets.giftVoucher.pagination.goto', {page: pageNum})) : (selected ? `Page ${pageNum}, page actuelle` : `Aller à la page ${pageNum}`);
          }
          if (type === 'first') {
            return t ? t('widgets.giftVoucher.pagination.first') : 'Aller à la première page';
          }
          if (type === 'last') {
            return t ? t('widgets.giftVoucher.pagination.last') : 'Aller à la dernière page';
          }
          if (type === 'next') {
            return t ? t('widgets.giftVoucher.pagination.next') : 'Aller à la page suivante';
          }
          if (type === 'previous') {
            return t ? t('widgets.giftVoucher.pagination.previous') : 'Aller à la page précédente';
          }
          return '';
        }}
        sx={{
          '& .MuiPaginationItem-root': {
            cursor: 'pointer',
            borderRadius: 2,
            fontWeight: 600,
            color: primaryColor ? primaryColor : 'primary.main',
          },
          '& .MuiPaginationItem-root.Mui-selected': {
            backgroundColor: primaryColor ? `${primaryColor} !important` : 'primary.main',
            color: '#fff !important',
          },
          '& .MuiPaginationItem-root.Mui-selected:hover': {
            backgroundColor: primaryColor ? `${primaryColor} !important` : 'primary.main',
            opacity: 0.85,
          },
        }}
      />
    </Stack>
  );
}

GiftVoucherPagination.propTypes = {
  page: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  primaryColor: PropTypes.string,
  t: PropTypes.func,
};

export default GiftVoucherPagination;
